/**
 * This component wraps React-Router's Link by providing name-based routing.
 *
 * The `name` prop should match a route in the flattened
 * routeConfiguration passed in context by the RoutesProvider
 * component. The `params` props is the route params for the route
 * path of the given route name.
 *
 * The `to` prop is an object with the same shape as Link requires,
 * but without `pathname` that will be generated from the given route
 * name.
 *
 * Some additional props can be passed for the <a> element like
 * `className` and `style`.
 *
 * The component can also be given the `activeClassName` prop that
 * will be added to the element className if the current URL matches
 * the one in the generated pathname of the link.
 */
import React, { useContext } from 'react';
import { number, object, string, shape, any } from 'prop-types';
import { ReactReduxContext } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useMyContextFunctions } from '../../context/ContextFunctions';
import { signsInWithModal } from '../../util/authPrompt';
import { LOGIN, SIGNUP } from '../../util/enums';

import { pathByRouteName, findRouteByRouteName } from '../../util/routes';

// A click the browser would follow in this tab (not a new tab or window)
const isPlainClick = e =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.defaultPrevented;

export const NamedLinkComponent = props => {
  const routeConfiguration = useRouteConfiguration();
  // Read as the link is clicked, so links don't re-render on sign-in; null outside a store
  const reduxContext = useContext(ReactReduxContext);
  const { onRequireAuth } = useMyContextFunctions() || {};
  const { name, params, title } = props;

  const onOver = () => {
    const { component: Page } = findRouteByRouteName(name, routeConfiguration);
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
  };

  // Link props
  const { to, children } = props;
  const pathname = pathByRouteName(name, routeConfiguration, params);
  const { match } = props;
  const active = match.url && match.url === pathname;

  // <a> element props
  const { className, style, activeClassName, id, tabIndex } = props;
  // aria-* attributes (aria-current, aria-label...) go straight to the <a>
  const ariaProps = Object.keys(props)
    .filter(k => k.startsWith('aria-'))
    .reduce((acc, k) => ({ ...acc, [k]: props[k] }), {});
  const aElemProps = {
    className: classNames(className, { [activeClassName]: active }),
    style,
    title,
    ...(id ? { id } : {}),
    ...(tabIndex != null ? { tabIndex } : {}),
    ...ariaProps,
  };

  // A guest's link to a page that needs an account: on desktop the auth modal opens over this
  // page, which keeps its place, and the link is followed once they're signed in
  const onClick = e => {
    const route = findRouteByRouteName(name, routeConfiguration);
    const isGuest = !!reduxContext && !reduxContext.store.getState().auth?.isAuthenticated;
    if (route?.auth && isGuest && onRequireAuth && isPlainClick(e) && signsInWithModal()) {
      e.preventDefault();
      onRequireAuth({
        authType: route.authPage === 'LoginPage' ? LOGIN : SIGNUP,
        to: `${pathname}${to.search || ''}${to.hash || ''}`,
      });
    }
  };

  return (
    <Link
      onMouseOver={onOver}
      onTouchStart={onOver}
      onClick={onClick}
      to={{ pathname, ...to }}
      {...aElemProps}
    >
      {children}
    </Link>
  );
};

NamedLinkComponent.defaultProps = {
  params: {},
  to: {},
  children: null,
  className: '',
  style: {},
  activeClassName: 'NamedLink_active',
  title: null,
  match: {},
};

// This ensures a nice display name in snapshots etc.
NamedLinkComponent.displayName = 'NamedLink';

NamedLinkComponent.propTypes = {
  // name of the route in routeConfiguration
  name: string.isRequired,
  // params object for the named route
  params: object,
  // Link component props
  to: shape({ search: string, hash: string, state: object }),
  children: any,

  // generic props for the underlying <a> element
  className: string,
  style: object,
  activeClassName: string,
  title: string,
  id: string,
  tabIndex: number,

  // from withRouter
  match: object,
};

const NamedLink = withRouter(NamedLinkComponent);
NamedLink.displayName = 'NamedLink';

export default NamedLink;
